/*
  "Complete the kit" add-on rows for the Motion product form.
  Rendered by snippets/product-upsell-addons.liquid; styled by assets/upsell-addons.css.

  theme.js is NOT modified. The only coupling is:
    - the product form id (data-form-id, "AddToCartForm-<section>-<product>")
    - the /cart/add.js endpoint (theme.routes.cartAdd)
    - the "ajaxProduct:added" / "ajaxProduct:error" events Motion already listens for,
      so the cart drawer rebuild + open and any other listeners work unchanged.

  Behaviour
    - Nothing ticked: this file does nothing and Motion's own submit handler runs.
    - Something ticked: intercept the form's submit in the CAPTURE phase, POST
      {items:[main, ...addons]} as JSON, then dispatch "ajaxProduct:added" on the form
      with the response in detail.product. A 422 goes into the form's .errors slot the
      way theme.AjaxProduct does.
    - "Buy it now" (dynamic checkout) is hidden while any add-on is ticked and returns
      when none are - the express button would skip the cart and drop the add-ons.
    - Sold-out add-ons arrive disabled from Liquid and are never submitted.
    - The element is rendered hidden and reveals itself on upgrade, so without JS, or inside
      the quick-shop modal (whose copied script never runs), the rows never show. If it does
      upgrade inside the modal it removes its own block (see connectedCallback).
*/
(function () {
  'use strict';

  if (!('customElements' in window) || customElements.get('upsell-addons')) return;

  var ROW = '[data-upsell-row]';
  var CHECK = '[data-upsell-check]';
  var VARIANT = '[data-upsell-variant]';
  var PRICE = '[data-upsell-price]';
  var ACTIVE_CLASS = 'upsell-addons-active';
  var GENERIC_ERROR = 'Sorry, those items could not be added to your cart. Please try again.';

  function cartAddUrl() {
    return (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';
  }

  class UpsellAddons extends HTMLElement {
    connectedCallback() {
      // Quick shop: Motion fills the modal by fetching the full product page and copying its
      // [data-product-blocks] in (theme.js openModalProduct), so the Liquid template guard does
      // not reach it. The modal's product container carries data-modal="true"; drop the block there.
      var modalContainer = this.closest('[data-modal="true"]');
      if (modalContainer) {
        var block = this.closest('.product-block--upsell-addons') || this;
        block.remove();
        return;
      }

      // Rendered hidden by Liquid; reveal now that the element has upgraded on a real product page.
      this.removeAttribute('hidden');

      this.loading = false;
      this._onChange = this._onChange.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._sync = this._sync.bind(this);

      this.addEventListener('change', this._onChange);

      // Capture phase, registered on document: this runs before Motion's own submit
      // listener on the form in every browser, whatever order the scripts loaded in.
      document.addEventListener('submit', this._onSubmit, true);

      if (this.form) {
        this._sync();
      } else {
        // The form is rendered by the buy_buttons block further down the section.
        document.addEventListener('DOMContentLoaded', this._sync, { once: true });
      }
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
      document.removeEventListener('submit', this._onSubmit, true);
      document.removeEventListener('DOMContentLoaded', this._sync);
      var form = this.form;
      if (form) {
        form.classList.remove(ACTIVE_CLASS);
        var dyn = form.querySelector('.shopify-payment-button');
        if (dyn) dyn.hidden = false;
      }
    }

    get form() {
      return this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    }

    get checks() {
      return Array.prototype.slice.call(this.querySelectorAll(CHECK));
    }

    ticked() {
      return this.checks.filter(function (c) { return c.checked && !c.disabled; });
    }

    _onChange(evt) {
      var row = evt.target.closest(ROW);
      if (!row) return;
      var check = row.querySelector(CHECK);

      if (evt.target.matches(VARIANT)) {
        var option = evt.target.options[evt.target.selectedIndex];
        if (option) {
          if (check) check.value = option.value;
          var price = row.querySelector(PRICE);
          if (price && option.dataset.price) price.textContent = option.dataset.price;
        }
      }

      row.classList.toggle('upsell-addon--checked', !!(check && check.checked));
      this._sync();
    }

    // Decision 2: hide "Buy it now" while any add-on is ticked; restore it when none are.
    _sync() {
      var form = this.form;
      if (!form) return;
      var active = this.ticked().length > 0;
      form.classList.toggle(ACTIVE_CLASS, active);
      var dyn = form.querySelector('.shopify-payment-button');
      if (dyn) dyn.hidden = active;
    }

    _onSubmit(evt) {
      var form = this.form;
      if (!form || evt.target !== form) return;

      var addons = this.ticked();
      if (addons.length === 0) return; // let Motion's handler run untouched

      evt.preventDefault();
      evt.stopImmediatePropagation();
      if (this.loading) return;

      var main = this._mainItem(form);
      if (!main) return;

      var items = [main].concat(addons.map(function (c) {
        return { id: parseInt(c.value, 10), quantity: 1 };
      }));

      var button = form.querySelector('[data-add-to-cart]');
      this.loading = true;
      if (button) button.classList.add('btn--loading');

      var self = this;
      fetch(cartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) { return response.json(); })
        .then(function (data) {
          if (data && data.status && data.status >= 400) {
            self._error(form, data);
          } else {
            self._success(form, button, data);
          }
        })
        .catch(function () {
          self._error(form, { description: GENERIC_ERROR });
        })
        .then(function () {
          self.loading = false;
          if (button) button.classList.remove('btn--loading');
        });
    }

    // Build the main line from the form the same way Motion's FormData submit would,
    // carrying variant id, quantity, selling plan and any properties[...] fields.
    _mainItem(form) {
      var data = new FormData(form);
      var id = parseInt(data.get('id'), 10);
      if (!id) return null;

      var item = { id: id, quantity: parseInt(data.get('quantity'), 10) || 1 };
      var sellingPlan = data.get('selling_plan');
      if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);

      var properties = {};
      var hasProperties = false;
      data.forEach(function (value, key) {
        var match = /^properties\[(.+)\]$/.exec(key);
        if (match && value !== '') {
          properties[match[1]] = value;
          hasProperties = true;
        }
      });
      if (hasProperties) item.properties = properties;
      return item;
    }

    _success(form, button, data) {
      var errors = form.querySelector('.errors');
      if (errors) errors.remove();

      if (window.theme && theme.settings && theme.settings.cartType === 'page') {
        window.location = theme.routes.cartPage;
        return;
      }

      // The add-ons are in the cart now: clear the ticks so "Buy it now" returns.
      this.checks.forEach(function (c) {
        c.checked = false;
        var row = c.closest(ROW);
        if (row) row.classList.remove('upsell-addon--checked');
      });
      this._sync();

      form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: { product: data, addToCartBtn: button },
        bubbles: true
      }));
    }

    // Mirrors theme.AjaxProduct.error(): message into a .errors div appended to the form.
    _error(form, error) {
      var message = error && (typeof error.description === 'object' ? error.message : error.description);
      if (!message) {
        console.warn(error);
        message = GENERIC_ERROR;
      }

      var existing = form.querySelector('.errors');
      if (existing) existing.remove();

      var errorDiv = document.createElement('div');
      errorDiv.classList.add('errors', 'text-center');
      errorDiv.textContent = message;
      form.append(errorDiv);

      form.dispatchEvent(new CustomEvent('ajaxProduct:error', {
        detail: { errorMessage: message },
        bubbles: true
      }));
    }
  }

  customElements.define('upsell-addons', UpsellAddons);
})();
